import type { Intl } from '@/intl'
import type { Bus, GroupResponsibleState, Participant, Room } from '@/scopes/account/group-responsible/domain/types'
import { formatDate } from '@/scopes/account/common/formatters'
import { getTransportType } from '../../selectors/busSelectors'
import { getHasEarlyDeparture, getHasSheets, getHasTowels, PARTICIPANT_SERVICE_FIELDS } from '../../selectors/participantSelectors'
import { getBusNumber } from '../../utils/busUtils'
import { getParticipantStatusLabel } from '../../utils/participants'

function valOrDash(value: string | number | undefined | null): string {
  return value != null && value !== '' ? String(value) : '-'
}

function formatService(service: Participant['skiPass'], intl: Intl): string {
  if (!service)
    return '-'
  return service.price > 0 ? `${service.description} - ${intl.formatCurrency(service.price)}` : valOrDash(service.description)
}

function formatYesNoService(
  service: Participant['sheets'],
  intl: Intl,
): string {
  const label = service ? intl.formatMessage({ id: 'yes_label' }) : '-'
  return service?.price && service.price > 0 ? `${label} - ${intl.formatCurrency(service.price)}` : label
}

function formatYesNoOnlyService(
  service: Participant['earlyDeparture'],
  intl: Intl,
): string {
  return service ? intl.formatMessage({ id: 'yes_label' }) : '-'
}

function calculateExtrasTotal(participant: Participant): number {
  return PARTICIPANT_SERVICE_FIELDS.reduce((sum, { getService }) => sum + (getService(participant)?.price ?? 0), 0)
}

function translateHeaders(intl: Intl, ids: string[]): string[] {
  return ids.map(id => intl.formatMessage({ id }))
}

interface OptionalServiceColumns {
  hasSheets: boolean
  hasTowels: boolean
  hasEarlyDeparture: boolean
}

function getParticipantHeaders(intl: Intl, isOpenSubscriptionModel: boolean, { hasSheets, hasTowels, hasEarlyDeparture }: OptionalServiceColumns): string[] {
  return translateHeaders(intl, [
    'groups_responsible_table_name_label',
    ...(isOpenSubscriptionModel ? ['common_status'] : []),
    'groups_responsible_room',
    ...(isOpenSubscriptionModel ? ['groups_responsible_table_amount_due_label'] : []),
    'email_address',
    'groups_responsible_booking_number',
    'groups_responsible_master_booking_number',
    'groups_responsible_phone',
    'groups_responsible_mobile_phone',
    'groups_responsible_accommodation',
    'groups_responsible_room_type',
    'groups_responsible_accommodation_arrival_date',
    'groups_responsible_accommodation_departure_date',
    'common_ski_pass',
    'common_ski_material',
    ...(hasSheets ? ['groups_responsible_sheets'] : []),
    ...(hasTowels ? ['groups_responsible_towels'] : []),
    ...(hasEarlyDeparture ? ['groups_responsible_early_departure'] : []),
    'groups_responsible_travel_insurance',
    'groups_responsible_cancel_insurance',
    'groups_responsible_policy_costs',
    'groups_responsible_size_of_sweater',
    'common_ski_lesson',
    'groups_responsible_total_extras',
  ])
}

function getRoomHeaders(intl: Intl): string[] {
  return translateHeaders(intl, [
    'groups_responsible_table_room_name_label',
    'groups_responsible_room_type',
    'groups_responsible_table_room_occupancy_label',
    'groups_responsible_accommodation',
    'groups_responsible_hosts',
  ])
}

function getBusHeaders(intl: Intl): string[] {
  return translateHeaders(intl, [
    'groups_responsible_table_name_label',
    'groups_responsible_table_bus_stop_label',
    'groups_responsible_table_bus_number_label',
    'email_address',
    'groups_responsible_phone',
    'groups_responsible_mobile_phone',
    'groups_responsible_accommodation',
    'groups_responsible_room_type',
    'common_ski_pass',
    'common_ski_material',
  ])
}

function getParticipantRoom(state: GroupResponsibleState, bookingCustomerId: number) {
  return Object.values(state.rooms).find(room => room.participantsInfo.some(p => p.bookingCustomerId === bookingCustomerId))
}

function formatParticipantRow(
  participant: Participant,
  roomData: Room | undefined,
  intl: Intl,
  isOpenSubscriptionModel: boolean,
  masterBookingNumber: number | undefined,
  { hasSheets, hasTowels, hasEarlyDeparture }: OptionalServiceColumns,
): string[] {
  const statusLabel = getParticipantStatusLabel(participant)
  const status = intl.formatMessage({ id: statusLabel.text })
  const room = participant.hasRoomAssigned ? intl.formatMessage({ id: 'groups_responsible_room_assigned' }) : intl.formatMessage({ id: 'groups_responsible_room_unassigned' })

  return [
    valOrDash(participant.name),
    ...(isOpenSubscriptionModel ? [status] : []),
    room,
    ...(isOpenSubscriptionModel ? [intl.formatCurrency(participant.amountDue)] : []),
    valOrDash(participant.email),
    valOrDash(participant.bookingNumber),
    valOrDash(masterBookingNumber),
    valOrDash(participant.telephoneNumber),
    valOrDash(participant.mobileNumber),
    valOrDash(roomData?.accommodationName),
    valOrDash(roomData?.type),
    valOrDash(formatDate(roomData?.accommodationStartDate)),
    valOrDash(formatDate(roomData?.accommodationEndDate)),
    formatService(participant.skiPass, intl),
    formatService(participant.skiMaterial, intl),
    ...(hasSheets ? [formatYesNoService(participant.sheets, intl)] : []),
    ...(hasTowels ? [formatYesNoService(participant.towels, intl)] : []),
    ...(hasEarlyDeparture ? [formatYesNoOnlyService(participant.earlyDeparture, intl)] : []),
    formatService(participant.travelInsurance, intl),
    formatService(participant.cancelInsurance, intl),
    formatService(participant.policyCosts, intl),
    formatService(participant.sweaterSize, intl),
    formatService(participant.skiLesson, intl),
    intl.formatCurrency(calculateExtrasTotal(participant)),
  ]
}

function formatBusParticipantRow(
  participant: Participant,
  roomData: Room | undefined,
  busData: Bus[] | undefined,
  intl: Intl,
): string[] {
  const [outboundBus, inboundBus] = [
    busData?.find(bus => bus.direction === 'Outbound'),
    busData?.find(bus => bus.direction === 'Inbound'),
  ]

  const formatWithCode = (name?: string, code?: string) => {
    if (!name)
      return ''
    return code ? `${name} [${code}]` : name
  }

  const departureLocation = outboundBus?.departureLocation
  const arrivalLocation = inboundBus?.arrivalLocation
  const departureLocationCode = outboundBus?.departureLocationCode
  const arrivalLocationCode = inboundBus?.arrivalLocationCode
  const busNumber = `${getBusNumber(outboundBus)}/${getBusNumber(inboundBus)}`

  let location = '-'

  if (departureLocation || arrivalLocation) {
    const dep = formatWithCode(departureLocation, departureLocationCode)
    const arr = formatWithCode(arrivalLocation, arrivalLocationCode)

    location = [dep, arr].filter(Boolean).join(' - ')
  }
  else if (participant.isRegistered) {
    location = intl.formatMessage({ id: 'groups_responsible_table_self_drive_label' })
  }

  return [
    valOrDash(participant.name),
    location,
    valOrDash(busNumber),
    valOrDash(participant.email),
    valOrDash(participant.telephoneNumber),
    valOrDash(participant.mobileNumber),
    valOrDash(roomData?.accommodationName),
    valOrDash(roomData?.type),
    formatService(participant.skiPass, intl),
    formatService(participant.skiMaterial, intl),
  ]
}

export function getParticipantTableData(
  state: GroupResponsibleState,
  intl: Intl,
): string[][] {
  const isOpenSubscriptionModel = state.bookingData.group.subscriptionModel === 'Open'
  const optionalServiceColumns: OptionalServiceColumns = {
    hasSheets: getHasSheets(state),
    hasTowels: getHasTowels(state),
    hasEarlyDeparture: getHasEarlyDeparture(state),
  }
  const tableData = [getParticipantHeaders(intl, isOpenSubscriptionModel, optionalServiceColumns)]

  if (state.participants) {
    for (const participant of Object.values(state.participants)) {
      const bookingCustomerId = Number(participant.bookingCustomerId)
      const roomData = getParticipantRoom(state, bookingCustomerId)
      tableData.push(formatParticipantRow(
        participant,
        roomData,
        intl,
        isOpenSubscriptionModel,
        state.bookingData.group.masterBookingNumber,
        optionalServiceColumns,
      ))
    }
  }

  return tableData
}

export function getRoomsTableData(
  state: GroupResponsibleState,
  intl: Intl,
): string[][] {
  const tableData = [getRoomHeaders(intl)]

  if (state.rooms) {
    for (const room of Object.values(state.rooms)) {
      const participants = room.participantsInfo?.length ? room.participantsInfo.map(p => p.name).join('\n') : '-'

      const type = room.type ? intl.formatMessage({ id: room.type }) : '-'

      tableData.push([
        valOrDash(room.name),
        type,
        valOrDash(room.occupancyText),
        valOrDash(room.accommodationName),
        participants,
      ])
    }
  }

  return tableData
}

export function getBusTableData(
  state: GroupResponsibleState,
  intl: Intl,
): string[][] {
  const tableData = [getBusHeaders(intl)]

  if (state.participants) {
    for (const participant of Object.values(state.participants)) {
      const bookingCustomerId = Number(participant.bookingCustomerId)
      const participantId = Number(participant.id)
      const transportType = getTransportType(participant?.id, state.buses, participant)
      if (transportType !== 'bus') {
        continue
      }

      const busData = Object.values(state.buses).filter(bus => bus.participantsId.includes(participantId))
      const roomData = getParticipantRoom(state, bookingCustomerId)

      tableData.push(formatBusParticipantRow(participant, roomData, busData, intl))
    }
  }

  return tableData
}

export function getTableData(
  state: GroupResponsibleState,
  intl: Intl,
): [
    string[][],
    string,
  ] {
  switch (state.activeTab) {
    case 'participants':
      return [
        getParticipantTableData(state, intl),
        intl.formatMessage({ id: 'common_participants' }),
      ]
    case 'rooms':
      return [getRoomsTableData(state, intl), intl.formatMessage({ id: 'groups_responsible_table_rooms' })]
    case 'bus':
      return [getBusTableData(state, intl), intl.formatMessage({ id: 'groups_responsible_table_bus_schedule' })]
    default:
      return [[], '']
  }
}
